import { useCallback, useMemo } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  STYLE_EXPOSURES_BY_INDUSTRY, STYLE_FACTOR_ATTRIBUTION, STYLE_FACTOR_EXPOSURES,
  STYLE_FACTOR_MODEL, STYLE_FACTOR_RETURNS, STYLE_FACTOR_RISK, STYLE_REALIZED_RISK, STYLE_FACTOR_SNAPSHOT,
  STYLE_FACTOR_STATUS, STYLE_HOLDING_ATTRIBUTION, STYLE_SPECIFIC_RETURNS, STYLE_ANALYSIS_HOLDINGS,
  STYLE_ATTRIBUTION_BY_GROUP, STYLE_ATTRIBUTION_BY_INDUSTRY, STYLE_EXPOSURES_BY_GROUP,
  STYLE_RISK_HISTORY, STYLE_HOLDING_CONTRIBUTIONS, STYLE_RISK_BY_HOLDING, EXPOSURES_LOOK_THROUGH, RISK_LOOK_THROUGH, VALUATION_LOOK_THROUGH, RETURNS_LOOK_THROUGH,
  SIMULATION_RISK_LOOK_THROUGH,
} from '../queries/styleFactors';
import { FUND_FACTOR_VIEW, PORTFOLIO_FACTOR_VIEW } from '../queries/factors';
import { asClient } from '../clientScope';
import { isActiveJob, useBackgroundJobs } from '../backgroundJobs';
import { ANALYSIS_KIND, STYLE_RISK_RUN } from '../../config/jobTypes';
import { rereadPastBackendCache } from './_shared';

import { hasFundLevel, simulationRoutes, simulationValues, withRoot } from '../../lib/lookThrough';

const READ = { fetchPolicy: 'cache-first', errorPolicy: 'all' };

/**
 * How one entity's stored rows are asked for, where the entity is a client's
 * portfolio: on the client's behalf (`X-Target-User-Id`), and again every
 * `refreshMs` while the job writing those rows is still running. Both default
 * to off, which is every read of a fund, a stock or a model.
 */
const forEntity = ({ clientUserId = null, refreshMs = 0 } = {}) => ({
  context: asClient(clientUserId),
  pollInterval: refreshMs,
});

/**
 * A |t| below this is noise. Plotting an insignificant factor return as signal
 * is the commonest way a cross-sectional model gets over-read.
 */
export const T_SIGNIFICANT = 2;

/**
 * Outside this band a "factor return" is one-signed enough not to be a spread.
 * See `positiveShare` for what it catches and why it is flagged, not fixed.
 */
export const ONE_SIDED = 0.9;

/**
 * Trailing windows for the factor-return table, in TRADING days.
 *
 * Trading days rather than calendar days because the series has a row per
 * fitted day and no weekends in it, so counting back 365 rows would be a year
 * and a half. The counts are measured off this data rather than assumed: 2,612
 * fitted days over 2016-01-28 to 2026-09-23 is ~246 a year, and using the
 * textbook 252 left the 3y column empty on a 3y fetch.
 */
export const RETURN_PERIODS = [['1m', 21], ['3m', 63], ['6m', 126], ['1y', 246], ['3y', 738]];

/**
 * The model reaches every kind of entity, and the label differs.
 */
export const ENTITY_LABEL = {
  stock: 'stock',
  mutual_fund: 'fund',
  asset_model: 'model',
  portfolio: 'portfolio',
};

/**
 * The style families, in the order a decomposition reads: the market first,
 * then the tilts, then what is left.
 */
export const RISK_FAMILIES = ['market', 'style', 'industry'];

/**
 * A table can still be behind its ORM — the resolver then surfaces a raw
 * Postgres error. Recognised so a screen can say so rather than showing the
 * reader `psycopg2.errors.UndefinedColumn`.
 */
export const isNotMigrated = (error) => {
  const text = [
    error?.message,
    ...(error?.graphQLErrors ?? []).map((e) => e?.message),
  ].filter(Boolean).join(' ');
  return /UndefinedColumn|does not exist/i.test(text);
};

const wrap = (rows, loading, error, extra = {}) => ({
  rows: rows ?? [],
  loading,
  error,
  /* Distinguished, because they need different words on screen. */
  pending: Boolean(error) && isNotMigrated(error),
  ...extra,
});

/** The model: its factors, what each means, and the industry buckets. */
export function useStyleFactorModel() {
  const { data, loading, error } = useQuery(STYLE_FACTOR_MODEL, READ);
  const spec = data?.fundamentalStyleFactorModel ?? null;
  return useMemo(() => ({
    spec,
    factors: spec?.styleFactors ?? [],
    industries: spec?.industryBuckets ?? [],
    version: spec?.version ?? null,
    loading,
    error,
  }), [spec, loading, error]);
}

/** One entity's style exposures through time, newest last. */
export function useStyleExposures(securitySymbol, { startDate, endDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_FACTOR_EXPOSURES, {
    variables: { securitySymbol, startDate: startDate ?? null, endDate: endDate ?? null },
    skip: skip || !securitySymbol,
    ...READ,
  });
  const rows = data?.fundamentalStyleFactorExposures;
  return useMemo(
    () => wrap(rows, loading, error, { latest: rows?.length ? rows[rows.length - 1] : null }),
    [rows, loading, error],
  );
}

/** What each factor earned per day across the market. */
export function useStyleFactorReturns({ factorNames, startDate, endDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_FACTOR_RETURNS, {
    variables: {
      factorNames: factorNames?.length ? factorNames : null,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    },
    skip,
    ...READ,
  });
  return useMemo(() => {
    const rows = data?.fundamentalStyleFactorReturns ?? [];

    /* Oldest first — the rows arrive in no guaranteed order and a cumulative
       path built out of order is not a path. */
    const ordered = [...rows].sort((a, b) => String(a.asOfDate).localeCompare(String(b.asOfDate)));
    const dates = [...new Set(ordered.map((r) => r.asOfDate))];

    /**
     * A DAILY factor return, ALREADY IN PERCENT — 0.39 is 0.39%, like every
     * other figure this model publishes.
     *
     * This is what made the returns view read as zeros. Compounding them as
     * fractions (`1 + 0.39`) is 39% a day: over the 2,612 days the unbounded
     * query returned, twelve of the thirteen styles collapsed to exactly −1 and
     * printed as "−1.00%", size came out at 9.26 and short-term momentum
     * overflowed to Infinity. Correctly compounded the same history gives
     * momentum +48%, value +20% and volatility −62%.
     */
    const grow = (acc, r) => acc * (1 + (Number(r) || 0) / 100);

    const byFactor = new Map();
    ordered.forEach((r) => {
      if (!byFactor.has(r.factorName)) {
        byFactor.set(r.factorName, {
          factorName: r.factorName,
          factorType: r.factorType,
          days: 0,
          cumulative: 1,
          significant: 0,
          positive: 0,
          /* date → cumulative growth so far, for the chart. */
          path: new Map(),
        });
      }
      const f = byFactor.get(r.factorName);
      f.days += 1;
      f.cumulative = grow(f.cumulative, r.factorReturn);
      f.path.set(r.asOfDate, f.cumulative);
      if ((Number(r.factorReturn) || 0) > 0) f.positive += 1;
      if (Math.abs(Number(r.tStatistic) || 0) >= T_SIGNIFICANT) f.significant += 1;
    });

    const summary = [...byFactor.values()]
      .map((f) => {
        /* A trailing window read straight off the cumulative path: the growth
           at the end divided by the growth `n` rows back. Null when the fetched
           range is shorter than the window, so a column says "not covered"
           rather than quietly reporting a shorter period under a longer name. */
        const levels = [...f.path.values()];
        const periods = {};
        RETURN_PERIODS.forEach(([id, n]) => {
          periods[id] = levels.length > n
            ? (levels[levels.length - 1] / levels[levels.length - 1 - n] - 1) * 100
            : null;
        });
        return {
          ...f,
          /* Kept in PERCENT, as everything else on these screens is. */
          cumulativeReturn: (f.cumulative - 1) * 100,
          periods,
          significantShare: f.days ? f.significant / f.days : 0,
          /**
           * The share of days the factor was positive — a check on whether this
           * is a factor return at all.
           *
           * A factor return is a long-short spread: it should be near-symmetric
           * around zero, and every style here is (49-59%). One that is positive
           * on 96% of days is not a spread, it is a drift, and compounding it
           * produces the four-figure totals that made this screen look broken
           * when the arithmetic was right. Flagged rather than hidden or
           * silently corrected — the number is what the model published.
           */
          positiveShare: f.days ? f.positive / f.days : 0,
        };
      })
      .sort((a, b) => b.cumulativeReturn - a.cumulativeReturn);

    /* The same paths as chartable series, in percent and rebased to 0 at the
       start of the window. A factor with no row on a date carries null rather
       than the previous level, so a gap draws as a gap. */
    const series = summary.map((f) => ({
      factorName: f.factorName,
      factorType: f.factorType,
      values: dates.map((d) => (f.path.has(d) ? (f.path.get(d) - 1) * 100 : null)),
    }));

    return { ...wrap(rows, loading, error), summary, series, dates };
  }, [data, loading, error]);
}

/** One security's return split into explained and specific. */
export function useStyleSpecificReturns(securitySymbol, { startDate, endDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_SPECIFIC_RETURNS, {
    variables: { securitySymbol, startDate: startDate ?? null, endDate: endDate ?? null },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const rows = data?.fundamentalStyleFactorSpecificReturns ?? [];
    /* Compounded, so "what the styles explained" and "what was this security"
       can be read side by side over the window. */
    const totals = rows.reduce((acc, r) => ({
      total: acc.total * (1 + (Number(r.totalReturn) || 0)),
      fitted: acc.fitted * (1 + (Number(r.fittedReturn) || 0)),
      specific: acc.specific * (1 + (Number(r.specificReturn) || 0)),
    }), { total: 1, fitted: 1, specific: 1 });
    return {
      ...wrap(rows, loading, error),
      totalReturn: totals.total - 1,
      fittedReturn: totals.fitted - 1,
      specificReturn: totals.specific - 1,
    };
  }, [data, loading, error]);
}

/** Return attributed to market, industry and each style over a window. */
export function useStyleAttribution(securitySymbol, { startDate, endDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_FACTOR_ATTRIBUTION, {
    variables: { securitySymbol, startDate: startDate ?? null, endDate: endDate ?? null },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const a = data?.equityStyleFactorAttribution ?? null;
    const period = a?.period ?? [];
    const group = (type) => period
      .filter((r) => r.componentType === type)
      .sort((x, y) => Math.abs(y.contribution) - Math.abs(x.contribution));
    return {
      attribution: a,
      styles: group('style'),
      industries: group('industry'),
      market: group('market'),
      specific: group('specific'),
      /* The server returns this alongside the rest and dropping it made the
         four groups add up to less than the window's return with nothing on
         screen to say why. */
      unexplained: group('unexplained'),
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
    };
  }, [data, loading, error]);
}

/**
 * Which holding contributed each piece of the entity's EXPOSURE.
 *
 * Point-in-time and keyed by nothing but the symbol — no date range, because
 * an exposure is a level on a day rather than something accumulated. Returns a
 * `byFactor` index as well as the rows, since the grid reads it that way.
 */
export function useStyleHoldingContributions(securitySymbol, { skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_HOLDING_CONTRIBUTIONS, {
    variables: { securitySymbol },
    skip: skip || !securitySymbol,
    ...READ,
  });
  const rows = data?.fundamentalStyleFactorHoldingContributions;
  return useMemo(() => {
    /* symbol → factor → contribution, which is the lookup the per-stock grid
       does once per cell. */
    const byHolding = new Map();
    (rows ?? []).forEach((r) => {
      const cells = {};
      (r.contributions ?? []).forEach((c) => { cells[c.factor] = Number(c.exposure) || 0; });
      byHolding.set(r.holdingSecuritySymbol, { weight: Number(r.weight) || 0, cells });
    });
    return {
      ...wrap(rows, loading, error, { byHolding, asOfDate: rows?.[0]?.asOfDate ?? null }),
    };
  }, [rows, loading, error]);
}

/**
 * A loader for the rows behind each style exposure, for the card to call the
 * first time a row is opened — nothing is fetched until then.
 *
 *   'stored'    fundamentalStyleFactorHoldingContributions — the nightly
 *               table; what the fund page reads.
 *   'analysis'  fundamentalStyleFactorAnalysis.holdingContributions —
 *               computed from the current book, so a portfolio booked today
 *               has them before the nightly run has written any.
 *
 * Both answer the same row type. Read through the Apollo cache, which is the
 * page's cache: a second open is instant, and the background-jobs tracker
 * evicts both fields when the portfolio's analysis finishes.
 */
export function useStyleHoldingsLoader(source, securitySymbol) {
  const client = useApolloClient();
  return useCallback(async () => {
    if (!securitySymbol) return [];
    const analysis = source === 'analysis';
    const { data } = await client.query({
      query: analysis ? STYLE_ANALYSIS_HOLDINGS : STYLE_HOLDING_CONTRIBUTIONS,
      variables: { securitySymbol },
      fetchPolicy: 'cache-first',
    });
    return (analysis
      ? data?.fundamentalStyleFactorAnalysis?.holdingContributions
      : data?.fundamentalStyleFactorHoldingContributions) ?? [];
  }, [client, source, securitySymbol]);
}

/**
 * The look-through as paths, for the Exposures panel — see EXPOSURES_LOOK_THROUGH.
 *
 * Read ONCE per panel rather than per opened row: one response carries the
 * sector, style and regression families, and a portfolio's rows run to a
 * thousand. `skip` while the panel is not on screen.
 *
 * `unsupported` is a server without the endpoint — staging, until the backend
 * branch lands there. Every drill then falls back to the flat per-stock list
 * it had before, so a reader loses the hierarchy, not the drill.
 */
export function useExposuresLookThrough(securitySymbol, { asOfDate = null, skip = false } = {}) {
  const { data, loading, error } = useQuery(EXPOSURES_LOOK_THROUGH, {
    variables: { securitySymbol, asOfDate },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const lt = data?.lookThroughContributions ?? null;
    /* ONLY the root field missing is "unsupported" — a server without the
       endpoint at all. Any other refusal (a field this document asks for that
       the server's version does not carry) is a real error and is surfaced:
       it used to be swallowed as "unsupported" and the panel fell back to the
       flat list in silence, which hid a version mismatch for a whole day. */
    const unsupported = Boolean(error)
      && /Cannot query field ['"]?lookThroughContributions['"]? on type/i.test(error.message || '');
    /* symbol → { key → own figure }, for the regression "fund's own" column. */
    const regressionOwn = new Map((lt?.regressionOwn ?? []).map((o) => [
      o.securitySymbol, new Map((o.values ?? []).map((v) => [v.key, v.value])),
    ]));
    /* bucket → item name → { weight, pathIds }: the rows inside each
       classification bucket, as the server grouped them. */
    const buckets = new Map((lt?.exposures ?? []).map((b) => [
      b.bucket,
      new Map((b.items ?? []).map((it) => [String(it.name ?? '').trim().toLowerCase(), {
        name: it.name, weight: it.weight, pathIds: new Set(it.pathIds ?? []),
      }])),
    ]));
    /* A fund's routes start at its holdings; the fund is put at their head so
       the Funds / Stocks readings mean the same on a fund page as on a model's
       — see withRoot. */
    const rows = lt && lt.entityType === 'mutual_fund' && !hasFundLevel(lt.rows)
      ? withRoot(lt.rows, { securitySymbol: lt.securitySymbol, kind: 'fund' })
      : (lt?.rows ?? []);
    return {
      lookThrough: lt,
      rows,
      exposures: lt?.styleExposures ?? [],
      buckets,
      regressionOwn,
      coverage: lt?.coverage ?? null,
      asOfDate: lt?.asOfDate ?? null,
      disclosureDate: lt?.disclosureDate ?? null,
      loading,
      error: unsupported ? null : error,
      unsupported,
      /* Nothing to drill into yet, for whatever reason — the one flag a
         drill reads to decide between this and its flat fallback. */
      ready: Boolean(lt) && !unsupported,
    };
  }, [data, loading, error]);
}

/**
 * The look-through as paths, for the Risk tab — see RISK_LOOK_THROUGH.
 *
 * Read once per tab. `kinds.total` / `kinds.active` carry each measurement's
 * summary and what its rows LEAVE: the benchmark-only names, summed whole and
 * by factor, which is the line a drill prints under an active decomposition.
 * `realized` is the window's σ the realised rows sum to.
 */
export function useRiskLookThrough(securitySymbol, { asOfDate = null, skip = false } = {}) {
  const { data, loading, error } = useQuery(RISK_LOOK_THROUGH, {
    variables: { securitySymbol, asOfDate, windowDays: 365 },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const lt = data?.lookThroughContributions ?? null;
    const unsupported = Boolean(error)
      && /Cannot query field ['"]?lookThroughContributions['"]? on type/i.test(error.message || '');
    const rows = lt && lt.entityType === 'mutual_fund' && !hasFundLevel(lt.rows)
      ? withRoot(lt.rows, { securitySymbol: lt.securitySymbol, kind: 'fund' })
      : (lt?.rows ?? []);
    const kind = (k) => {
      if (!k) return null;
      const only = k.benchmarkOnly ?? [];
      const byFactor = new Map();
      only.forEach((b) => (b.byFactor ?? []).forEach((c) => {
        byFactor.set(c.componentName, (byFactor.get(c.componentName) ?? 0) + (Number(c.contribution) || 0));
      }));
      return {
        ...k,
        benchmarkOnlyTotal: only.reduce((t, b) => t + (Number(b.totalContribution) || 0), 0),
        benchmarkOnlySpecific: only.reduce((t, b) => t + (Number(b.specificContribution) || 0), 0),
        benchmarkOnlyByFactor: byFactor,
        benchmarkOnlyCount: only.length,
      };
    };
    return {
      lookThrough: lt,
      rows,
      kinds: { total: kind(lt?.riskTotal), active: kind(lt?.riskActive) },
      realized: lt?.realizedRisk ?? null,
      asOfDate: lt?.asOfDate ?? null,
      disclosureDate: lt?.disclosureDate ?? null,
      loading,
      error: unsupported ? null : error,
      unsupported,
      ready: Boolean(lt) && !unsupported,
    };
  }, [data, loading, error]);
}

/**
 * The look-through as paths, for the Valuations tab — see VALUATION_LOOK_THROUGH.
 *
 * `specs` is metric → harmonic; `own` is symbol → metric → the level-1
 * holding's own figure, for the "fund's own" reading beside the mean of its
 * stocks.
 */
export function useValuationLookThrough(securitySymbol, { asOfDate = null, skip = false } = {}) {
  const { data, loading, error } = useQuery(VALUATION_LOOK_THROUGH, {
    variables: { securitySymbol, asOfDate },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const lt = data?.lookThroughContributions ?? null;
    const unsupported = Boolean(error)
      && /Cannot query field ['"]?lookThroughContributions['"]? on type/i.test(error.message || '');
    const rows = lt && lt.entityType === 'mutual_fund' && !hasFundLevel(lt.rows)
      ? withRoot(lt.rows, { securitySymbol: lt.securitySymbol, kind: 'fund' })
      : (lt?.rows ?? []);
    return {
      lookThrough: lt,
      rows,
      specs: new Map((lt?.fundamentalSpecs ?? []).map((s) => [s.metric, Boolean(s.harmonic)])),
      own: new Map((lt?.fundamentalsOwn ?? []).map((o) => [
        o.securitySymbol, new Map((o.values ?? []).map((v) => [v.key, v.value])),
      ])),
      coverage: lt?.coverage ?? null,
      asOfDate: lt?.asOfDate ?? null,
      disclosureDate: lt?.disclosureDate ?? null,
      loading,
      error: unsupported ? null : error,
      unsupported,
      ready: Boolean(lt) && !unsupported,
    };
  }, [data, loading, error]);
}

/**
 * The look-through as paths, for simulation risk under one scenario — see
 * SIMULATION_RISK_LOOK_THROUGH.
 *
 * `rows` are the served routes PLUS one "Other" leaf under each level-1
 * holding, summing the priced leaves no route carries (`unmapped`, by `via`).
 * With them every grouping ties: a fund's children add to the fund's own
 * stored row, and the funds to the scenario's total. In the Stocks reading
 * the Others gather into one row, which is what they are — the part of the
 * book the look-through does not reach a stock for. A level-1 STOCK the routes
 * do not carry is given back as itself rather than as an Other of itself.
 *
 * `own` is symbol → { volatility, var95, … }: each level-1 holding's whole
 * stored row, for the figure a fund is reconciled against.
 */
export function useSimulationRiskLookThrough(securitySymbol, { scenario = 'standard_var', riskType = 'RISK', skip = false } = {}) {
  const { data, loading, error } = useQuery(SIMULATION_RISK_LOOK_THROUGH, {
    variables: { securitySymbol, scenario, riskType },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const lt = data?.lookThroughContributions ?? null;
    /* A server without the look-through at all, or one that has it but not
       this family yet — either way there is nothing to draw and nothing wrong. */
    const unsupported = Boolean(error)
      && /Cannot query field ['"]?(lookThroughContributions|simulationRisk)['"]? on type/i.test(error.message || '');
    const sim = lt?.simulationRisk ?? null;
    const rooted = Boolean(lt) && lt.entityType === 'mutual_fund' && !hasFundLevel(lt.rows);
    const served = rooted
      ? withRoot(lt.rows, { securitySymbol: lt.securitySymbol, kind: 'fund' })
      : (lt?.rows ?? []);

    return {
      lookThrough: lt,
      rows: sim ? simulationRoutes(served, sim.unmapped) : [],
      summary: sim,
      own: new Map((sim?.own ?? []).map((o) => [o.securitySymbol, simulationValues(o.values)])),
      source: sim?.source ?? null,
      runDate: sim?.simulationRunDate ?? null,
      disclosureDate: lt?.disclosureDate ?? null,
      loading,
      error: unsupported ? null : error,
      unsupported,
      ready: Boolean(sim) && !unsupported,
    };
  }, [data, loading, error]);
}

/** The horizons the returns look-through is asked for, as the resolver names them. */
export const RETURN_HORIZONS = [
  ['1d', '1D'], ['1w', '1W'], ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['ytd', 'YTD'], ['1y', '1Y'],
];
const RETURN_HORIZON_IDS = RETURN_HORIZONS.map(([id]) => id);

/**
 * The look-through as paths, for return contributions — see RETURNS_LOOK_THROUGH.
 *
 * `own` is symbol → horizon → the level-1 holding's own contribution (its own
 * return × its weight), and `ownTotals` is horizon → what those add up to: the
 * figure the routes are reconciled against.
 */
export function useReturnsLookThrough(securitySymbol, { asOfDate = null, skip = false } = {}) {
  const { data, loading, error } = useQuery(RETURNS_LOOK_THROUGH, {
    variables: { securitySymbol, asOfDate, horizons: RETURN_HORIZON_IDS },
    skip: skip || !securitySymbol,
    ...READ,
  });
  return useMemo(() => {
    const lt = data?.lookThroughContributions ?? null;
    const unsupported = Boolean(error)
      && /Cannot query field ['"]?lookThroughContributions['"]? on type/i.test(error.message || '');
    const rows = lt && lt.entityType === 'mutual_fund' && !hasFundLevel(lt.rows)
      ? withRoot(lt.rows, { securitySymbol: lt.securitySymbol, kind: 'fund' })
      : (lt?.rows ?? []);
    const ownTotals = new Map();
    const own = new Map((lt?.returnsOwn ?? []).map((o) => [
      o.securitySymbol,
      new Map((o.values ?? []).map((v) => {
        ownTotals.set(v.key, (ownTotals.get(v.key) ?? 0) + (Number(v.value) || 0));
        return [v.key, v.value];
      })),
    ]));
    return {
      lookThrough: lt,
      rows,
      own,
      ownTotals,
      asOfDate: lt?.asOfDate ?? null,
      disclosureDate: lt?.disclosureDate ?? null,
      loading,
      error: unsupported ? null : error,
      unsupported,
      ready: Boolean(lt) && !unsupported,
    };
  }, [data, loading, error]);
}

/** Which holding contributed each piece of that attribution. */
export function useStyleHoldingAttribution(securitySymbol, { startDate, endDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_HOLDING_ATTRIBUTION, {
    variables: { securitySymbol, startDate, endDate },
    skip: skip || !securitySymbol || !startDate || !endDate,
    ...READ,
  });
  const a = data?.equityStyleFactorHoldingAttribution ?? null;
  return useMemo(() => ({
    attribution: a,
    ...wrap(a?.holdings, loading, error),
  }), [a, loading, error]);
}

/** Style exposures rolled up by industry. `weighting` is "cap" or "equal". */
export function useStyleExposuresByIndustry({ asOfDate, weighting = 'cap', skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_EXPOSURES_BY_INDUSTRY, {
    variables: { asOfDate: asOfDate ?? null, weighting },
    skip,
    ...READ,
  });
  return useMemo(
    () => wrap(data?.fundamentalStyleFactorExposuresByIndustry, loading, error),
    [data, loading, error],
  );
}

/**
 * One entity on one day: exposures, that day's attribution, and risk.
 *
 * The snapshot the API guide recommends — three reads in one call. Works for a
 * stock, a fund, a model variant or a portfolio; `entityType` in the answer
 * says which, and `risk` is empty for a stock.
 */
export function useStyleSnapshot(securitySymbol, { asOfDate, skip, clientUserId, refreshMs } = {}) {
  const { data, loading, error } = useQuery(STYLE_FACTOR_SNAPSHOT, {
    variables: { securitySymbol, asOfDate: asOfDate ?? null },
    skip: skip || !securitySymbol,
    ...READ,
    ...forEntity({ clientUserId, refreshMs }),
  });
  return useMemo(
    () => styleSnapshotOf(data?.fundamentalStyleFactors, { loading, error }),
    [data, loading, error],
  );
}

/**
 * The snapshot view of a `fundamentalStyleFactors` payload, however it
 * arrived — from its own query above, or selected on a fund or portfolio
 * record (FACTOR_VIEW_FIELDS) and handed to StyleFactors as a prop.
 */
export function styleSnapshotOf(payload, { loading = false, error = null } = {}) {
  /* The top-level resolver answers with a list of days, latest first; the
     field on a record is the one day. */
  const snap = Array.isArray(payload) ? payload[0] ?? null : payload ?? null;
  const attribution = snap?.latestAttribution ?? [];
  const by = (type) => attribution
    .filter((r) => r.componentType === type)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  return {
    snapshot: snap,
    entityType: snap?.entityType ?? null,
    exposures: [...(snap?.exposures ?? [])].sort(
      (a, b) => Math.abs(b.exposure) - Math.abs(a.exposure),
    ),
    attribution,
    styles: by('style'),
    industries: by('industry'),
    market: by('market'),
    specific: by('specific'),
    unexplained: by('unexplained'),
    risk: snap?.risk ?? [],
    loading,
    error,
    pending: Boolean(error) && isNotMigrated(error),
  };
}

/**
 * The two factor tabs' data for one fund or portfolio — style factors,
 * regression loadings and the Top-500 baseline — from one selection
 * (FACTOR_VIEW_FIELDS) whichever it is. `kind` is 'fund' or 'portfolio' (a
 * model or goal is a portfolio). Pass `skip` until a factor tab is open: the
 * style snapshot carries a risk decomposition nobody else on the page needs.
 */
export function useFactorView(kind, symbol, { skip = false, clientUserId = null } = {}) {
  const isFund = kind === 'fund';
  const off = skip || !symbol;
  /* A portfolio read on a client's behalf, as usePortfolio does it. */
  const context = useMemo(() => asClient(clientUserId), [clientUserId]);
  const { data, loading, error, client } = useQuery(isFund ? FUND_FACTOR_VIEW : PORTFOLIO_FACTOR_VIEW, {
    variables: { symbol },
    context,
    skip: off,
    ...READ,
  });
  /* A portfolio's view past the backend's cache, for when its analysis
     finishes. Nothing to do for a scheme, or for a view nobody has open. */
  const forceRefetch = useCallback(async () => {
    if (isFund || off) return null;
    return rereadPastBackendCache(client, { query: PORTFOLIO_FACTOR_VIEW, variables: { symbol }, context });
  }, [client, context, isFund, off, symbol]);
  return useMemo(() => {
    const record = isFund ? data?.getMetaMutualFund : data?.getPortfolio?.portfolio;
    return {
      styleFactors: record?.fundamentalStyleFactors ?? null,
      factorExposures: record?.factorExposures ?? null,
      benchmarkFactorExposures: record?.benchmarkFactorExposures ?? null,
      /* Asked and answered — so a null above is "not computed", not "not yet
         asked", and the tab can say which. */
      loaded: Boolean(data) && !loading,
      loading,
      error,
      forceRefetch,
    };
  }, [isFund, data, loading, error, forceRefetch]);
}

/**
 * The full risk decomposition, both kinds unless one is asked for.
 *
 * Everything here is ALREADY a percentage — `totalRisk` 14.48 means 14.48%
 * annualised — so nothing is scaled on the way through.
 */
export function useStyleRisk(securitySymbol, { asOfDate, riskKind, skip, clientUserId, refreshMs } = {}) {
  const { data, loading, error } = useQuery(STYLE_FACTOR_RISK, {
    variables: {
      securitySymbol,
      asOfDate: asOfDate ?? null,
      riskKind: riskKind ?? null,
    },
    skip: skip || !securitySymbol,
    ...READ,
    ...forEntity({ clientUserId, refreshMs }),
  });
  return useMemo(() => {
    const rows = data?.fundamentalStyleFactorRisk ?? [];
    const pick = (kind) => rows.find((r) => r.riskKind === kind) ?? null;
    /* A fund's per-factor rows come back with exposure and volatility zeroed;
       only the contribution means anything, so a screen showing a factor table
       has to know whether it has more than that. */
    const detailed = (row) => (row?.factors ?? []).some((f) => Number(f.exposure) !== 0);
    return {
      rows,
      total: pick('total'),
      active: pick('active'),
      /* Per kind, because one can carry exposures while the other does not and
         a table drawn from the wrong flag is a column of zeros. */
      hasFactorDetail: detailed(pick('total')) || detailed(pick('active')),
      detailFor: { total: detailed(pick('total')), active: detailed(pick('active')) },
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
    };
  }, [data, loading, error]);
}

/**
 * THE REALISED SPLIT of the same risk, by factor AND by holding.
 *
 * Read off the same row as the forecast, but it answers a different question:
 * not what the book is expected to do but what its weight path DID over the
 * trailing window (`observations` days). Every cell is a covariance with the
 * total, and covariances are additive, so one set of numbers reads both ways:
 * a factor summed over the names is that factor's contribution, and a name
 * summed over the components is that holding's.
 *
 * UNITS ARE PERCENT, as everywhere in this model: 14.26 is 14.26% annualised.
 *
 * SPECIFIC RISK IS NOT A FIELD HERE. The forecast row publishes
 * `specificContribution`; the realised columns do not, and the model states
 * the identity instead — the factor rows plus the specific part make the
 * total. So it is summed from the holdings' OWN `specificContribution`, which
 * are reported numbers, rather than taken as the leftover of a subtraction.
 * The two agree to six decimals on the fund sampled (0.693320 against
 * 0.693316); summing reported cells is preferred because a residual would also
 * silently absorb any factor row the server failed to send.
 *
 * FETCHED SEPARATELY from `useStyleRisk` and skipped unless asked for: the
 * ACTIVE row carries a cell for every benchmark constituent, not just the
 * holdings — 622 names and ~945KB of JSON on the fund sampled — which no
 * forecast view needs.
 */
export function useRealizedRisk(securitySymbol, { asOfDate, riskKind, skip } = {}) {
  const { data, loading, error, refetch } = useQuery(STYLE_REALIZED_RISK, {
    variables: {
      securitySymbol,
      asOfDate: asOfDate ?? null,
      riskKind: riskKind ?? null,
    },
    skip: skip || !securitySymbol,
    ...READ,
  });

  return useMemo(() => {
    const rows = data?.fundamentalStyleFactorRisk ?? [];

    const shape = (row) => {
      /* A row exists for the forecast whether or not the realised split could
         be computed — it needs a holdings history, and a book younger than the
         minimum window has none. Null, not an empty decomposition, so the view
         can say which it is. */
      if (row?.realizedTotalRisk == null) return null;

      const holdings = (row.realizedHoldingContributions ?? []).map((h) => ({
        symbol: h.holdingSecuritySymbol,
        industry: h.industry ?? null,
        weight: Number(h.weight) || 0,
        total: Number(h.totalContribution) || 0,
        factor: Number(h.factorContribution) || 0,
        specific: Number(h.specificContribution) || 0,
        specificVolatility: Number(h.specificVolatility) || 0,
        /* Keyed by factor name so a factor's drill is a lookup per holding
           rather than a scan of every holding's list. */
        byFactor: new Map((h.byFactor ?? []).map((c) => [c.componentName, Number(c.contribution) || 0])),
      })).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

      const factors = (row.realizedFactors ?? [])
        .map((f) => ({ name: f.factorName, type: f.factorType, contribution: Number(f.contribution) || 0 }))
        .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

      const specificContribution = holdings.reduce((t, h) => t + h.specific, 0);

      return {
        riskKind: row.riskKind,
        asOfDate: row.asOfDate,
        benchmarkSymbol: row.benchmarkSymbol ?? null,
        entityType: row.entityType ?? null,
        totalRisk: Number(row.realizedTotalRisk),
        factorRisk: row.realizedFactorRisk == null ? null : Number(row.realizedFactorRisk),
        specificRisk: row.realizedSpecificRisk == null ? null : Number(row.realizedSpecificRisk),
        observations: row.realizedObservations ?? null,
        families: (row.realizedFamilyContributions ?? [])
          .map((f) => ({ key: f.componentName, value: Number(f.contribution) || 0 })),
        factors,
        specificContribution,
        holdings,
      };
    };

    const pick = (kind) => shape(rows.find((r) => r.riskKind === kind));
    const total = pick('total');
    const active = pick('active');
    return {
      total,
      active,
      /* The model covers this entity but has not measured it — a different
         thing from the model not covering it at all, which is `error`. */
      has: Boolean(total || active),
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
      /* For after a model run: the cached answer is the empty one. */
      refetch,
    };
  }, [data, loading, error, refetch]);
}

/** Whether the model has been fitted recently. */
export function useStyleStatus({ lookbackDays = 10, skip = false, fresh = false } = {}) {
  const { data, loading, error, refetch } = useQuery(STYLE_FACTOR_STATUS, {
    variables: { lookbackDays },
    skip,
    ...READ,
    /* `fresh` for a screen about to act on the answer: a status cached earlier
       in the session can say the model is behind when it has since caught up. */
    ...(fresh ? { fetchPolicy: 'cache-and-network' } : {}),
  });
  const s = data?.fundamentalStyleFactorStatus ?? null;
  return {
    status: s,
    modelVersion: s?.modelVersion ?? null,
    isCurrent: s?.isCurrent ?? null,
    latestFittedDate: s?.latestFittedDate ?? null,
    latestFittableDate: s?.latestFittableDate ?? null,
    /* Always a list, so "is the model behind" is one length check. */
    missingDates: Array.isArray(s?.missingDates) ? s.missingDates : (s?.missingDates ? [s.missingDates] : []),
    loading,
    error,
    refetch,
  };
}

/* ── grouped roll-ups ───────────────────────────────────────────────────── */

/** How a roll-up weights its members. */
export const GROUP_WEIGHTINGS = [['cap', 'Cap weighted'], ['equal', 'Equal weighted']];

/** Industry is the built-in grouping; any security group is the other axis. */
export const INDUSTRY_GROUPING = 'industry';

/**
 * Attribution rolled up by industry, or by any security group.
 *
 * Both take a SINGLE `asOfDate` — there is no period form, so a run of days
 * would be a call per day. The screen offers a date, not a window, because
 * that is what the API can honestly answer.
 */
export function useStyleAttributionByGroup({
  securityGroupId = null, asOfDate = null, weighting = 'cap', skip = false,
} = {}) {
  const byIndustry = securityGroupId == null;
  const { data, loading, error } = useQuery(
    byIndustry ? STYLE_ATTRIBUTION_BY_INDUSTRY : STYLE_ATTRIBUTION_BY_GROUP,
    {
      variables: byIndustry
        ? { asOfDate, weighting }
        : { securityGroupId: Number(securityGroupId), asOfDate, weighting },
      skip,
      ...READ,
    },
  );
  return useMemo(() => {
    const rows = (byIndustry
      ? data?.equityStyleFactorAttributionByIndustry
      : data?.equityStyleFactorAttributionBySecurityGroup) ?? [];
    return {
      rows: [...rows].sort((a, b) => b.weightShare - a.weightShare),
      asOfDate: rows[0]?.asOfDate ?? null,
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
    };
  }, [data, byIndustry, loading, error]);
}

/** Exposures rolled up the same two ways. */
export function useStyleExposuresByGroup({
  securityGroupId = null, asOfDate = null, weighting = 'cap', skip = false,
} = {}) {
  const byIndustry = securityGroupId == null;
  const { data, loading, error } = useQuery(
    byIndustry ? STYLE_EXPOSURES_BY_INDUSTRY : STYLE_EXPOSURES_BY_GROUP,
    {
      variables: byIndustry
        ? { asOfDate, weighting }
        : { securityGroupId: Number(securityGroupId), asOfDate, weighting },
      skip,
      ...READ,
    },
  );
  return useMemo(() => {
    const rows = (byIndustry
      ? data?.fundamentalStyleFactorExposuresByIndustry
      : data?.fundamentalStyleFactorExposuresBySecurityGroup) ?? [];
    return {
      rows: [...rows].sort((a, b) => b.weightShare - a.weightShare),
      asOfDate: rows[0]?.asOfDate ?? null,
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
    };
  }, [data, byIndustry, loading, error]);
}

/**
 * Where a portfolio's style-risk job has got to, read off the background-jobs
 * tracker — which is what polls; nothing here does.
 *
 * Two rows matter, both for this portfolio: the analysis (A) and the style
 * job it queues as its last step (B, `fundamental_style_factor.run`). B only
 * counts when it was queued by the newest A — an earlier run's row says
 * nothing about this one.
 *
 *   idle       nothing in hand, or a run that was cancelled — the card shows
 *              whatever is stored, and an empty one offers to calculate
 *   preparing  A is going, or B is queued or still expected
 *   running    days are being stored; the card re-reads as they land
 *   done       B finished. An empty card now is a portfolio the model cannot
 *              measure, not one waiting to be calculated
 *   failed     B stopped; what was stored stays, and a retry continues from it
 *   missed     A finished and B never appeared — nothing was calculated
 *
 * `analysing` and `awaited` are the tracker's own answers for A and B, which
 * count work that is expected as well as work that is listed. `since` is
 * when B was queued, for telling a long run from a slow one.
 */
export function styleRiskJobOf(jobs, securitySymbol, { analysing = false, awaited = false } = {}) {
  /* Newest first, so the first match of each is the run that counts. */
  const mine = jobs.filter((j) => j.subject === securitySymbol);
  const a = mine.find((j) => j.kind === ANALYSIS_KIND) ?? null;
  const b = mine.find((j) => j.eventType === STYLE_RISK_RUN) ?? null;
  const since = b?.enqueuedAt ?? null;
  const active = Boolean(b) && isActiveJob(b);
  if (active && b.state === 'RUNNING') return { phase: 'running', since };
  if (active || analysing || awaited) return { phase: 'preparing', since };
  /* Unreadable times are given the benefit of the doubt. */
  const current = Boolean(b) && !(a && Date.parse(b.enqueuedAt ?? '') < Date.parse(a.enqueuedAt ?? ''));
  if (current) {
    return { phase: b.state === 'DONE' ? 'done' : b.state === 'FAILED' ? 'failed' : 'idle', since };
  }
  return { phase: a?.state === 'DONE' ? 'missed' : 'idle', since: null };
}

export function useStyleRiskJob(securitySymbol) {
  const { jobs, isActive } = useBackgroundJobs();
  const analysing = isActive(securitySymbol, ANALYSIS_KIND);
  const awaited = isActive(securitySymbol, STYLE_RISK_RUN);
  return useMemo(
    () => styleRiskJobOf(jobs, securitySymbol, { analysing, awaited }),
    [jobs, securitySymbol, analysing, awaited],
  );
}

/**
 * Risk through time.
 *
 * `riskKind` must be given — the point-in-time read returns both when it is
 * omitted, this one does not. Oldest first, so a chart reads left to right.
 */
export function useStyleRiskHistory(securitySymbol, {
  riskKind = 'total', startDate = null, endDate = null, skip = false, clientUserId, refreshMs,
} = {}) {
  const { data, loading, error } = useQuery(STYLE_RISK_HISTORY, {
    variables: { securitySymbol, riskKind, startDate, endDate },
    skip: skip || !securitySymbol,
    ...READ,
    ...forEntity({ clientUserId, refreshMs }),
  });
  return useMemo(() => {
    const rows = [...(data?.fundamentalStyleFactorRiskHistory ?? [])]
      .sort((a, b) => String(a.asOfDate).localeCompare(String(b.asOfDate)));
    return {
      rows,
      series: rows.map((r) => Number(r.totalRisk) || 0),
      /* The family split through time, as one series per family. */
      families: [...new Set(rows.flatMap((r) => (r.familyContributions ?? []).map((f) => f.componentName)))]
        .map((familyName) => ({
          name: familyName,
          series: rows.map((r) => Number(
            (r.familyContributions ?? []).find((f) => f.componentName === familyName)?.contribution,
          ) || 0),
        })),
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
    };
  }, [data, loading, error]);
}

/**
 * What is driving the risk — by STOCK and by FACTOR off the same sigma.
 *
 * The panel this feeds is the answer to "why is the tracking error 5.5%": a
 * stock-by-stock contribution, each openable into the factors behind it, and
 * the same total read the other way round as a factor list.
 *
 * `checks` is not decoration. The resolver states two identities — the holdings
 * sum to the total risk, and so do the factors plus the specific total — and a
 * screen that prints a decomposition without testing them is asserting
 * something it has not verified. Both are reported so the panel can say when
 * they do not hold rather than drawing a decomposition that does not add up.
 */
export function useStyleRiskByHolding(securitySymbol, { riskKind = 'total', asOfDate, skip } = {}) {
  const { data, loading, error } = useQuery(STYLE_RISK_BY_HOLDING, {
    variables: { securitySymbol, asOfDate: asOfDate ?? null, riskKind },
    skip: skip || !securitySymbol,
    ...READ,
  });

  return useMemo(() => {
    const r = data?.fundamentalStyleFactorRiskByHolding ?? null;
    const holdings = r?.holdings ?? [];
    const factors = r?.factors ?? [];
    const sum = (rows, pick) => rows.reduce((t, x) => t + (Number(pick(x)) || 0), 0);
    const byStock = sum(holdings, (h) => h.totalContribution);
    const byFactor = sum(factors, (f) => f.contribution) + (Number(r?.specificTotal) || 0);
    /* Risk is in percent like everything else here, so a tenth of a point is a
       generous tolerance on a number near 5 or 14. */
    const near = (a, b) => Math.abs(a - b) < 0.1;
    return {
      loading,
      error,
      pending: Boolean(error) && isNotMigrated(error),
      risk: r,
      holdings,
      factors,
      checks: r ? {
        byStock: near(byStock, r.totalRisk),
        byFactor: near(byFactor, r.totalRisk),
        stockTotal: byStock,
        factorTotal: byFactor,
      } : null,
    };
  }, [data, loading, error]);
}
